import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Layer Selector Modal - React Implementation
 *
 * Allows users to customize which layers appear in their layer selector.
 * Replaces the legacy Bootstrap modal with shadcn Dialog.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Label } from '../../../../components/ui/label';
import { useLayerCatalog } from '../../hooks/useLayerCatalog';
import { isLayerVisibleInSelector, setLayerVisibility, clearAllPreferences, getLayerKey, } from '../../utils/layerControlBridge';
const LayerSelectorModalReact = ({ isOpen, onClose }) => {
    const [layers, setLayers] = useState([]);
    const { fetchCatalog, refresh, isLoading: loading, error: catalogError } = useLayerCatalog();
    const [error, setError] = useState(null);
    // Load layers when modal opens
    useEffect(() => {
        if (isOpen) {
            loadLayers();
        }
    }, [isOpen]);
    // Sync catalog error to local state
    useEffect(() => {
        if (catalogError) {
            setError(catalogError);
        }
    }, [catalogError]);
    const loadLayers = async () => {
        setError(null);
        try {
            // Force refresh to get latest data
            const catalog = await fetchCatalog(true);
            if (!catalog) {
                throw new Error('Failed to fetch layer catalog');
            }
            const allLayers = {};
            // Flatten all layers from all categories
            Object.values(catalog.layers || {}).forEach((category) => {
                Object.assign(allLayers, category);
            });
            setLayers(Object.values(allLayers));
        }
        catch (err) {
            console.error('Error loading layers:', err);
            setError('Failed to load layers. Please try again.');
        }
    };
    const isLayerVisible = (layer) => {
        return isLayerVisibleInSelector(layer);
    };
    const toggleLayerVisibility = (layer) => {
        // Use layer_key first (for UPRN datasets) to match getLayerKey() in layerControlBridge
        const layerId = getLayerKey(layer);
        if (layerId) {
            const currentVisibility = isLayerVisible(layer);
            setLayerVisibility(layerId, !currentVisibility);
            // Force re-render
            setLayers([...layers]);
        }
    };
    const resetToDefaults = () => {
        clearAllPreferences();
        setLayers([...layers]);
    };
    // Separate layers into categories
    const systemLayers = layers.filter((l) => !l.layer_key || !l.layer_key.startsWith('uprn_dataset:'));
    const uprnDatasets = layers.filter((l) => l.layer_key && l.layer_key.startsWith('uprn_dataset:'));
    const myDatasets = uprnDatasets.filter((d) => d.is_owner);
    const sharedDatasets = uprnDatasets.filter((d) => !d.is_owner);
    // Group system layers by their groups
    const layersByGroup = {};
    systemLayers.forEach((layer) => {
        const groups = layer.groups || ['Other'];
        groups.forEach((group) => {
            if (group !== 'uprn_datasets') {
                if (!layersByGroup[group]) {
                    layersByGroup[group] = [];
                }
                layersByGroup[group].push(layer);
            }
        });
    });
    // Check if all layers in a group are visible
    const isGroupAllVisible = (groupLayers) => {
        return groupLayers.every((layer) => isLayerVisible(layer));
    };
    // Check if some (but not all) layers in a group are visible
    const isGroupPartiallyVisible = (groupLayers) => {
        const visibleCount = groupLayers.filter((layer) => isLayerVisible(layer)).length;
        return visibleCount > 0 && visibleCount < groupLayers.length;
    };
    // Toggle all layers in a group
    const toggleGroup = (groupLayers) => {
        const allVisible = isGroupAllVisible(groupLayers);
        const newVisibility = !allVisible;
        groupLayers.forEach((layer) => {
            const layerId = getLayerKey(layer);
            if (layerId) {
                setLayerVisibility(layerId, newVisibility);
            }
        });
        // Force re-render
        setLayers([...layers]);
    };
    return (_jsx(Dialog, { open: isOpen, onOpenChange: (open) => !open && onClose(), children: _jsxs(DialogContent, { className: "max-w-3xl max-h-[80vh]", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "Customise Layer Selector" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "Choose which layers appear in your layer selector. Changes are saved to your browser." })] }), _jsxs("div", { className: "overflow-auto max-h-[calc(80vh-180px)] space-y-6", children: [loading && (_jsxs("div", { className: "text-center py-8", children: [_jsx("div", { className: "inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" }), _jsx("p", { className: "mt-2 text-sm text-muted-foreground", children: "Loading layers..." })] })), error && (_jsx("div", { className: "text-center py-4 text-destructive", children: _jsx("p", { children: error }) })), !loading && !error && layers.length === 0 && (_jsx("p", { className: "text-center text-muted-foreground py-4", children: "No layers available." })), !loading && !error && layers.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold mb-3", children: "System Layers" }), Object.keys(layersByGroup)
                                            .sort()
                                            .map((groupName) => {
                                            const groupLayers = layersByGroup[groupName];
                                            const allVisible = isGroupAllVisible(groupLayers);
                                            const partiallyVisible = isGroupPartiallyVisible(groupLayers);
                                            return (_jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "flex items-center space-x-2 mb-2", children: [_jsx(Checkbox, { id: `group_${groupName}`, checked: allVisible, ref: (el) => {
                                                                    if (el) {
                                                                        el.indeterminate = partiallyVisible;
                                                                    }
                                                                }, onCheckedChange: () => toggleGroup(groupLayers) }), _jsx(Label, { htmlFor: `group_${groupName}`, className: "text-sm font-medium text-muted-foreground uppercase cursor-pointer", children: groupName })] }), _jsx("div", { className: "space-y-2 ml-6", children: layersByGroup[groupName].map((layer) => {
                                                            const layerId = layer.id || layer.layer_key;
                                                            const isVisible = isLayerVisible(layer);
                                                            return (_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Checkbox, { id: `layer_${layerId}`, checked: isVisible, onCheckedChange: () => toggleLayerVisibility(layer) }), _jsxs(Label, { htmlFor: `layer_${layerId}`, className: "cursor-pointer", children: [_jsx("div", { children: layer.name }), _jsxs("div", { className: "text-xs text-muted-foreground", children: [layer.type.toUpperCase(), " layer"] })] })] }, layerId));
                                                        }) })] }, groupName));
                                        })] }), uprnDatasets.length > 0 && (_jsxs("div", { className: "pt-4 border-t", children: [_jsx("h3", { className: "text-lg font-semibold mb-3", children: "UPRN Datasets" }), _jsx("p", { className: "text-sm text-muted-foreground mb-3", children: "User-created and shared UPRN datasets. Hidden by default." }), myDatasets.length > 0 && (_jsxs("div", { className: "mb-4", children: [_jsx("h4", { className: "text-sm font-medium text-muted-foreground uppercase mb-2", children: "My Datasets" }), _jsx("div", { className: "space-y-2 ml-2", children: myDatasets.map((dataset) => {
                                                        const layerId = dataset.layer_key;
                                                        const isVisible = isLayerVisible(dataset);
                                                        const shareIcon = dataset.share_mode === 'all'
                                                            ? '🌐'
                                                            : dataset.share_mode === 'specific'
                                                                ? '👥'
                                                                : '🔒';
                                                        return (_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Checkbox, { id: `layer_${layerId}`, checked: isVisible, onCheckedChange: () => toggleLayerVisibility(dataset) }), _jsxs(Label, { htmlFor: `layer_${layerId}`, className: "cursor-pointer", children: [_jsxs("div", { children: [shareIcon, " ", dataset.name] }), _jsxs("div", { className: "text-xs text-muted-foreground", children: [dataset.row_count || 0, " UPRNs"] })] })] }, layerId));
                                                    }) })] })), sharedDatasets.length > 0 && (_jsxs("div", { children: [_jsx("h4", { className: "text-sm font-medium text-muted-foreground uppercase mb-2", children: "Shared with Me" }), _jsx("div", { className: "space-y-2 ml-2", children: sharedDatasets.map((dataset) => {
                                                        const layerId = dataset.layer_key;
                                                        const isVisible = isLayerVisible(dataset);
                                                        return (_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Checkbox, { id: `layer_${layerId}`, checked: isVisible, onCheckedChange: () => toggleLayerVisibility(dataset) }), _jsxs(Label, { htmlFor: `layer_${layerId}`, className: "cursor-pointer", children: [_jsx("div", { children: dataset.name }), _jsxs("div", { className: "text-xs text-muted-foreground", children: [dataset.row_count || 0, " UPRNs"] })] })] }, layerId));
                                                    }) })] }))] }))] }))] }), _jsxs(DialogFooter, { children: [_jsx(Button, { variant: "outline", onClick: resetToDefaults, children: "Reset to Defaults" }), _jsx(Button, { onClick: onClose, children: "Done" })] })] }) }));
};
export default LayerSelectorModalReact;
